import type { MouseEvent, SyntheticEvent } from 'react'
import { legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { createPagingModel, dispatchSubsNavigation, type PagingTarget } from '@/msgdeskRuntime/subsNavigation'

// Modern paging look — self-contained (markup + wiring + Tailwind).
// Same behaviour and the same load-bearing hook classes as ../Legacy
// (md-paging / md-paging__page / md-paging__sequence / md-hook-disabled) so the
// legacy prev/next click router keeps resolving; the look is re-skinned purely
// through the scoped `md-paging--modern` class.

function stopEvent(event: SyntheticEvent) {
  event.preventDefault()
  event.stopPropagation()
}

function hookAttrs(className: string, legacyClass: string) {
  return {
    className,
    'data-md-legacy-class': legacyClass,
  }
}

export function PagingBar({
  section,
  currentPage = 1,
  totalPages = 1,
  registerContract = true,
}: {
  section: 0 | 1
  currentPage?: number
  totalPages?: number
  registerContract?: boolean
}) {
  const contract = section === 0 ? 'subs.paging.answers' : 'subs.paging.submsges'
  const paging = createPagingModel(currentPage, totalPages)
  const rootClass = 'md-hook-no-select md-paging md-paging--modern inline-flex flex-[0_0_auto] select-none items-center gap-0.5 rounded-[10px] border border-[rgba(15,23,42,0.06)] bg-slate-100 p-[3px] text-xs leading-none font-[Arial,Tahoma,Verdana,sans-serif]'
  const pageClass = 'md-paging__page inline-flex h-[26px] min-w-7 cursor-pointer items-center justify-center rounded-[7px] bg-transparent px-2 font-semibold text-slate-600 no-underline transition-[background,color,box-shadow] duration-150 hover:bg-white hover:text-[#2563eb] hover:shadow-[0_1px_3px_rgba(15,23,42,0.12)]'
  const currentClass = `${pageClass} md-paging__page--current md-hook-current-page cursor-default bg-[#2563eb] text-white shadow-[0_2px_8px_rgba(37,99,235,0.35)] hover:bg-[#2563eb] hover:text-white`
  const disabledClass = `${pageClass} md-hook-disabled cursor-default text-slate-300 shadow-none hover:bg-transparent hover:text-slate-300 hover:shadow-none`

  // BEHAVIOUR: jump to a page / step prev-next through the runtime bridge.
  function pageClick(event: MouseEvent<HTMLElement>, page: PagingTarget) {
    stopEvent(event)
    if (page === 'previous' && paging.previousDisabled) return
    if (page === 'next' && paging.nextDisabled) return
    dispatchSubsNavigation({ action: 'set-page', section, page, target: event.currentTarget })
  }

  return (
    <div
      {...(registerContract
        ? legacyHookAttrs(contract, rootClass)
        : {
            className: rootClass,
            'data-md-section': section === 0 ? 'answers' : 'submsges',
          })}
    >
      {paging.tokens.map((token, index) => token === 'gap' ? (
        <span key={`gap-${index}`} className="md-paging__gap inline-flex min-w-[18px] items-center justify-center text-[13px] text-slate-400">…</span>
      ) : (
        <a
          key={token}
          href="#"
          {...hookAttrs(
            token === paging.currentPage
              ? currentClass
              : pageClass,
            token === paging.currentPage ? 'mcss-current_page-cssm' : 'mcss-page_el-cssm',
          )}
          onClick={(e) => pageClick(e, token)}
        >
          {token}
        </a>
      ))}
      {/* Disabled edge buttons stay MOUNTED but display:none — the legacy
          prev/next click router resolves prev vs next by index inside this
          sequence span, so unmounting Prev would misroute Next as "prev". */}
      <span className="md-paging__sequence ml-1 inline-flex items-center gap-0.5">
        <a
          href="#"
          {...hookAttrs(
            paging.previousDisabled ? disabledClass : pageClass,
            paging.previousDisabled ? 'mcss-str_disable-cssm' : 'mcss-page_el-cssm',
          )}
          style={paging.previousDisabled ? { display: 'none' } : undefined}
          onClick={(e) => pageClick(e, 'previous')}
        >
          <span className="sr-only">Previous</span>
          <span aria-hidden="true" className="text-sm font-semibold leading-none">‹</span>
        </a>
        <a
          href="#"
          {...hookAttrs(
            paging.nextDisabled ? disabledClass : pageClass,
            paging.nextDisabled ? 'mcss-str_disable-cssm' : 'mcss-page_el-cssm',
          )}
          style={paging.nextDisabled ? { display: 'none' } : undefined}
          onClick={(e) => pageClick(e, 'next')}
        >
          <span className="sr-only">Next</span>
          <span aria-hidden="true" className="text-sm font-semibold leading-none">›</span>
        </a>
      </span>
    </div>
  )
}
